// The display names for muscles and body parts (#4210 Phase E).
//
// These used to be English string literals inside `shared/` — `BodyPart.name` in
// `fitness-constants.ts` and `MuscleNames` in `workout-solver-interface.ts`. They
// are user-visible text, so they now live in `client/src/assets/i18n/en.json` and
// are translated like every other UI string. `shared/` keeps only the ids.
//
// There are two label sets on purpose. They name overlapping muscles with
// different wording, because they answer different questions:
//
//   - `bodyParts` labels the 12 rows a user chooses a priority for on the
//     Physique Priority -> Custom screen. It groups muscles the way a user thinks
//     about them ("Shoulders (all heads)").
//   - `volumeTable` labels the 15 muscles the solver reports weekly set volume
//     for in the Muscle Volume panel. It splits those groups apart, because the
//     solver counts them separately ("Side Delts", "Rear Delts", "Upper Traps").
//
// Both use switch-on-literal with a `never` default, the same shape as
// `fieldDiffLabel` in services/exercise-diff-ui.ts. That keeps typesafe-i18n's
// typed accessors (an index expression would silently regress on a rename) and
// makes the `never` default a compile error the moment a body part or muscle is
// added without a label here — which is the whole reason the ids were narrowed
// to closed unions. There is no runtime "unknown id" case left to handle.

import { BodyPartId } from '@cf-shared/fitness-constants';
import { MuscleId } from '@cf-shared/workout-solver-interface';
import { LL } from 'i18n/i18n.service';

/** The label for one row of the Physique Priority -> Custom screen. */
export function getBodyPartLabel(bodyPartId: BodyPartId): string {
  const labels = LL.muscles.bodyParts;
  switch (bodyPartId) {
    case 'glutes': return labels.glutes();
    case 'glutemed': return labels.glutemed();
    case 'quads': return labels.quads();
    case 'hams': return labels.hams();
    case 'calves': return labels.calves();
    case 'shoulders': return labels.shoulders();
    case 'chest': return labels.chest();
    case 'lats': return labels.lats();
    case 'traps': return labels.traps();
    case 'triceps': return labels.triceps();
    case 'biceps': return labels.biceps();
    case 'abs': return labels.abs();
    default: { const _: never = bodyPartId; return _; }
  }
}

/** The label for one muscle row of the Muscle Volume panel. */
export function getMuscleVolumeLabel(muscleId: MuscleId): string {
  const labels = LL.muscles.volumeTable;
  switch (muscleId) {
    case 'chest': return labels.chest();
    case 'ldelt': return labels.ldelt();
    case 'rdelt': return labels.rdelt();
    case 'utraps': return labels.utraps();
    case 'lats': return labels.lats();
    case 'biceps': return labels.biceps();
    case 'triceps': return labels.triceps();
    case 'erectors': return labels.erectors();
    case 'quads': return labels.quads();
    case 'hams': return labels.hams();
    case 'glutes': return labels.glutes();
    case 'glutemed': return labels.glutemed();
    case 'gastrocnemius': return labels.gastrocnemius();
    case 'soleus': return labels.soleus();
    case 'abs': return labels.abs();
    default: { const _: never = muscleId; return _; }
  }
}
